<template>
    <ArticleDialog
        :title="payload ? payload.title : ''"
        :body="body"
        :img="payload ? payload.img : undefined"
    >
        <Paper
            v-if="articles && articles.length > 0"
            :header="
                $t('companyDetail.articles.moreArticlesFrom', {
                    companyName: company.data.name,
                })
            "
        >
            <div v-if="!articles">{{ $t('general.loading') }}...</div>
            <Carousel
                v-else
                :component="getComponent()"
                :items="articles"
                :noResourcesMessage="$t('companyDetail.articles.noMoreArticles')"
            />
        </Paper>
    </ArticleDialog>
</template>

<script>
// Components
import ArticleDialog from '~/shared/ArticleDialog.vue';
import ArticlesCarouselItem from '~/components/miscellaneous/Articles.CarouselItem';

// Utils
import AnalyticService from '~/services/AnalyticService';
import { mapArticles } from '~/utils/mappers/mediaMappers';
import { numberIdFromParam } from '~/utils/urlUtils';

export default {
    name: 'Articles.DialogItem',
    components: { ArticleDialog },
    props: {
        suppressLogging: Boolean,
    },
    data() {
        return {
            height: 600,
            width: 600,

            hasLoggedEvent: false, // Indicates if there has been a log of the article view
        };
    },
    computed: {
        companyId() {
            return numberIdFromParam(this.$route.params.id);
        },
        isOnCompanyPage() {
            return this.$route.name === 'companydetail';
        },
        payload() {
            return this.$interfaces.dialogModal.getState().payload;
        },
        hasHistory() {
            return this.$interfaces.dialogModal.hasHistory();
        },
        company() {
            if (this.isOnCompanyPage) {
                return this.$interfaces.company.id(this.companyId).get({ reactive: true });
            } else {
                return undefined;
            }
        },
        orgProfile() {
            if (!this.company || this.company.loading) return { loading: true };
            return this.$interfaces.organization.profile
                .id(this.company.data.externalId)
                .get({ reactive: true });
        },
        articles() {
            if (!this.payload) {
                return undefined;
            }

            if (this.isOnCompanyPage) {
                if (this.orgProfile.loading) return;
                return mapArticles(
                    this.orgProfile.data.articles.filter(it => it.id != this.payload.id)
                );
            } else {
                return undefined;
            }
        },
        article() {
            if (!this.payload) {
                return undefined;
            }

            return this.$interfaces.organization.article
                .id(this.payload.id)
                .get({ reactive: true });
        },
        body() {
            if (this.payload && this.payload.content) return this.payload.content;
            if (!this.article || this.article.loading) return;
            return this.article.data.body;
        },
    },
    methods: {
        closeDialog() {
            this.$interfaces.dialogModal.getState().resolver.res();
        },
        handleResizeEvent() {
            this.height = window.innerHeight * 0.9;
            this.width = window.innerWidth;
        },
        getComponent() {
            return ArticlesCarouselItem;
        },
        stepBackHistory() {
            this.$interfaces.dialogModal.popState();
        },
        async logArticleViewEvent() {
            if (this.suppressLogging) {
                return;
            }

            if (!this.isOnCompanyPage) {
                return;
            }

            const article = this.article ? this.article.data : null;

            // Not log if already logged, or if there is no data to log with
            if (this.hasLoggedEvent || (!article || !article.id)) {
                return;
            }

            // Making sure the company-data is fetched before we log
            const company = await this.$interfaces.company.id(this.companyId).get();
            if (!company || !company.data || !company.data.id) {
                return;
            }

            // Start logging
            this.hasLoggedEvent = true;
            AnalyticService.articleViewEvent(article.id, company.data.id, article.title);
        },
    },
    mounted() {
        this.$nextTick(() => {
            window.addEventListener('resize', this.handleResizeEvent);
        });
        this.handleResizeEvent();

        // Logging article view event. Will not log if the data is not in there store.
        // Therefore, this method is also being called in watch when the data has been fetched.
        this.logArticleViewEvent();
    },
    destroyed() {
        window.removeEventListener('resize', this.handleResizeEvent);
    },
    watch: {
        article(newArticle, oldArticle) {
            if (!newArticle || !oldArticle) {
                return;
            }

            if (!this.hasLoggedEvent || newArticle.ids !== oldArticle.ids) {
                this.logArticleViewEvent();
            }
        },
    },
};
</script>

<style lang="css" scoped>
.article-dialog {
    max-width: var(--grid-width);
    width: var(--grid-width);
    margin: 0 auto;
}

.wrapper-div {
    width: 100%;
    height: 100%;
    overflow-y: scroll;
    overflow-x: hidden;
}

.article-img {
    width: 100%;
    max-height: 600px;
    object-fit: cover;
    padding: 12px;
}
</style>
